import { AxiosError, AxiosResponse } from 'axios'
import lodash from 'lodash'
import { isPC, PathParamLossError, sleep, toastMessage } from '@xt/client/experimental/request/utils'
import { EnumHttpCode, EnumRequestError, EnumResponseErrorCode, getRequestTime } from '..'
import { ArmsSumKeys } from '@xt/client/enums/system/arms'
import $global from '@xt/client/store/global'
import { AccountAlertInfo } from '@xt/client/entities/user/types'
import { parseAccountBanInfo } from '@xt/client/utils/user'
import { ResponseError } from '@xt/client/experimental/request/types'
import { reportEntryLog, reportSumLog } from '@xt/client/utils/log'

const defaultErrMsg = '网络开小差了!'

/** 错误数据统一化 */
const getErrorResponseData = (errorResponse: AxiosResponse): ResponseError => {
  const responseType = errorResponse.config.responseType
  let errCode: number = EnumRequestError.XHR_API_FAILED
  let errMsg: string = defaultErrMsg
  let httpCode: number = errorResponse.status
  if (responseType === 'json' || responseType === 'text') {
    try {
      const {
        errCode: respErrCode,
        errMsg: respErrMsg,
        error_description,
        error
      } = responseType === 'json' ? errorResponse.data : JSON.parse(errorResponse.data || '{}')
      errCode = respErrCode || errCode
      errMsg = lodash.head(lodash.compact([respErrMsg, error_description, error, defaultErrMsg]))
    } catch {}
  }
  return { errCode, errMsg, httpCode }
}

export const dataResponseInterceptors = {
  success: async (response: AxiosResponse) => {
    return response
  },
  fail: async (error: AxiosError) => {
    const userStore = window.$nuxt.$store.$storeUser
    // 处理路径参数丢失
    if (error instanceof PathParamLossError) {
      if (window.__bl) {
        window.__bl.api(error.url, false, Date.now() - getRequestTime(), -1, error.message, getRequestTime())
        window.__bl.sum(ArmsSumKeys.PATH_PARAMTER_LOSS)
      }
      console.error('缺少path参数', error.message)
      toastMessage({ content: '缺少path参数' })
      throw { errCode: EnumRequestError.PATH_PARAM_LOSS, errMsg: 'path参数异常' }
    }

    if (!error.response || !error.status) {
      reportEntryLog('debug.fetch.no-response', {
        response: typeof error.response,
        status: typeof error.status,
        isAxiosError: error.isAxiosError,
        message: error.message,
        code: error.code,
        config: error.config
          ? {
              url: `${error.config.baseURL}${error.config.url}`,
              method: error.config.method,
              client: error.config.headers['x-xt-client-info'],
              timeout: error.config.timeout
            }
          : 'NONE',
        name: error.name
      })

      throw { errCode: EnumRequestError.NETWORK_ERROR, errMsg: '网络异常' }
    }

    const { errCode, errMsg, httpCode } = getErrorResponseData(error.response)
    console.warn(errCode, errMsg, httpCode, 'fail dataResponseInterceptors')

    // 调用oauth  设备禁用/用户封禁
    if (
      httpCode === EnumHttpCode.Forbidden &&
      [EnumResponseErrorCode.DEVICE_BAN, EnumResponseErrorCode.ACCOUNT_BAN, EnumResponseErrorCode.POSITION_BAN].includes(errCode)
    ) {
      const banInfo = errMsg
      if (isPC) {
        userStore.onToggleVisible(false)
      }
      try {
        // 获取封禁弹窗数据
        const _alertInfo: AccountAlertInfo = JSON.parse(banInfo)
        const alertInfo = parseAccountBanInfo(_alertInfo)
        userStore.setAccountBanInfo(alertInfo)
      } catch {
        reportSumLog('sys.ban.parse-error')
      }
      return
    }

    // 账号注销中  进入冷静期页面
    if (httpCode === EnumHttpCode.Forbidden && errCode === EnumResponseErrorCode.ACCOUNT_CANCELLING) {
      errMsg && toastMessage({ content: errMsg })
      await sleep(500)
      userStore.onUpdatingUserInfo()
      return
    }

    // 没有权限进入
    if (httpCode === EnumHttpCode.Unauthorized) {
      // 账号已注销
      if (errCode === EnumResponseErrorCode.ACCOUNT_CANCEL) {
        errMsg && toastMessage({ content: errMsg })
        await sleep(500)
      }
      await userStore.onOutLogin()
      toastMessage({ content: '登录失效' })
      // 内嵌不准反回首页
      if ($global.isEmbeddedH5Page) {
        wx.miniProgram.redirectTo({ url: `/pages/login/index?fallback=${encodeURIComponent(location.href)}` })
        return
      }
      await sleep(500)
      window.location.assign(window.location.origin + $global.base)
      return
    }

    // 登录的临时token失效
    if (errCode === EnumResponseErrorCode.TEMP_TOKEN_EXPIRED) {
      toastMessage({ content: '验证码获取失败，请重新登录' })
      await sleep()
      history.back()
      return
    }

    // errCode第一位或第四位是2  不弹toast提示
    if (/^(2|\d{3}2)/.test(`${errCode}`)) {
      throw { errCode, errMsg, httpCode }
    }

    errMsg && toastMessage({ content: errMsg })

    throw { errCode, errMsg, httpCode }
  }
}
